// Browser polyfills
import 'whatwg-fetch';

// Libs
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Layout, Result, Spin } from 'antd';
import NackleMenu from '@nackle/menu';
const { Content } = Layout;

// Redux
import appStore from './app-store';
import appAuth from './app-auth';
import { Provider, connect } from 'react-redux';
import {
    loadBundles,
    setUserDetails
} from './app-dux/app-dux.js';

// Routing
import { Route, routeHistory } from '@nackle/biw-web-router';
import { routes, bundleRoutingMiddleware } from './app-routing.jsx';

// Bridge
import { Bridge } from '@nackle/bridge';
import auth, { authRouteMiddleware, authRequestMiddleware } from './app-auth.js';

// Fetch
import { applyRequestMiddleware } from '../app/utils/fetch';

// Components
import { AppHeader, AppFooter, AppError } from '@nackle/studio-tools';

import './styles/style.less';

/*******************************************************************************
 *  1. App (root level component)
 */
class App extends React.Component {
    state = {
        loaded: false,
        cmxBundleLoaded: false
    };

    handleSignIn = async ( authState ) => {
        await routeHistory.refresh();
        await this.props.setUserDetails( authState?.claims );
        await this.props.loadBundles( [ 'navigation', 'studio-in-context-help' ] );

        this.setState( {
            loaded: true,
            cmxBundleLoaded: true
        } );
    };

    handleCompanyChange = async ( company ) => {
        routeHistory.push( `/${ company.id }` );
    };

    render() {
        const { loaded, cmxBundleLoaded } = this.state;
        return (
            <AppError>
                <Route render={ ( route ) => {
                    // set the title
                    document.title = route?.match ? route.match?.title : 'Gift Codes';
                    const companyId = route.state?.params?.companyId;

                    return (
                        <Bridge
                            auth={ appAuth }
                            onSignIn={ this.handleSignIn }
                        >
                            {
                                loaded && (
                                    <Layout className="root-layout">
                                        <AppHeader
                                            auth={ appAuth }
                                            companyId={ companyId }
                                            onCompanyChange={ this.handleCompanyChange }
                                        />
                                        <Layout>
                                            <NackleMenu
                                                auth={ auth }
                                                cmxBundleLoaded={ cmxBundleLoaded }
                                            />
                                            <Layout className="page-layout">
                                                {
                                                    this.props.loading &&
                                                    <div className="loading-overlay">
                                                        <div className="loading-indicator">
                                                            <Spin size="large" />
                                                        </div>
                                                    </div>
                                                }
                                                <Content className="app-content">
                                                    {
                                                        route.match ?
                                                            React.cloneElement( route.component, { companyId } )
                                                            : (
                                                                <Result
                                                                    status="404"
                                                                    title="404"
                                                                    subTitle="Sorry, the page you visited does not exist."
                                                                />
                                                            )
                                                    }
                                                </Content>
                                                <AppFooter />
                                            </Layout>
                                        </Layout>
                                    </Layout>
                                )
                            }
                        </Bridge>
                    );
                } } />
            </AppError>
        );
    }
}

/*******************************************************************************
 *  2. Connect App component to the Redux store
 */
App = connect(
    ( state ) => {
        return {
            loading: state.app.loading
        };
    },
    {
        loadBundles,
        setUserDetails
    }
)( App );

/*******************************************************************************
 *  3. Connect Routing
 */
routeHistory.applyRoutes( routes );
routeHistory.applyMiddleware( [ authRouteMiddleware, bundleRoutingMiddleware ] );

/*******************************************************************************
 *  4. Connect Auth to Fetch
 */
applyRequestMiddleware( authRequestMiddleware );


/*******************************************************************************
 * 5. Render the the app
 */

const rootElement = document.getElementById( 'app-root' );
const root = createRoot( rootElement );

root.render(
    <Provider store={ appStore }>
        <App />
    </Provider>
);

// Conditionally enable hot module reloading
if ( module.hot ) {
    module.hot.accept();
}
